<script setup lang="ts">
import { onMounted, ref } from 'vue';
import AltraIcon from "~/components/AltraIcon.vue";
import iconSolution from "~/components/icons/iconSolution.vue";
import iconChemicals from "~/components/icons/iconChemicals.vue";
import iconManufactured from "~/components/icons/iconManufactured.vue";
import iconManufacturing from "~/components/icons/iconManufacturing.vue";
import iconIndustry from "~/components/icons/iconIndustry.vue";
import iconApplications from "~/components/icons/iconApplications.vue";


const router = useRouter()
const { $io } = useNuxtApp()

const headerFixed = computed(() => $io.headerFixed);

let activeDropdownEl: HTMLElement | null = null;
let closeDropdownTimeout: number | null = null;
const globalNavRef = ref<HTMLElement | null>(null);

// Menu data matching the exact paths from mega menu components
const menuItems = {
  about: [
    { 
      label: '360 Services', 
      path: '/our-service',
      icon: 'services' // Special case - inline SVG
    },
    { 
      label: 'About Us', 
      path: '/about-us',
      icon: 'about'
    }
  ],
  catelogue: [
    { 
      label: 'Kitchen Products', 
      path: '/products/kitchen',
      icon: 'about'
    },
    { 
      label: 'Housekeeping Products', 
      path: '/products/house-keeping',
      icon: 'about'
    },
    { 
      label: 'Laundry Products', 
      path: '/products/laundry',
      icon: 'about'
    },
    { 
      label: 'Hand Hygiene Products', 
      path: '/products/hand-hygiene',
      icon: 'about'
    },
    { 
      label: 'Food Service Products', 
      path: '/products/food-and-beverages',
      icon: 'about'
    },
    { 
      label: 'Disinfectant Wipes', 
      path: '/products/disinfectant-wipes',
      icon: 'about'
    }
  ],
  products: [
    { 
      label: 'All Products', 
      path: '/products',
      icon: 'about'
    }
  ],
  media: [
    { 
      label: 'Blog & News', 
      path: '/blog',
      icon: 'about'
    },
    { 
      label: 'All Media', 
      path: '/media',
      icon: 'about'
    }
  ]
};

function openDropdown(el: HTMLElement) {
  if (!globalNavRef.value) return;
  const container = globalNavRef.value;
  if (activeDropdownEl === el) return;
  
  // Close any open dropdown first
  closeDropdown();
  
  container.classList.add('dropdownActive');
  activeDropdownEl = el;

  // Mark this rootLink active, remove from others
  const allRoots = Array.from(container.querySelectorAll('.hasDropdown'));
  allRoots.forEach(r => r.classList.remove('active'));
  el.classList.add('active');

  // Show the corresponding dropdown and position it relative to the menu item
  const dropdownName = el.getAttribute('data-dropdown');
  if (dropdownName) {
    const dropdown = container.querySelector(`.dropdownSection[data-dropdown="${dropdownName}"]`) as HTMLElement;
    if (dropdown) {
      dropdown.classList.add('active');
      
      // Position dropdown relative to the menu item
      const elRect = el.getBoundingClientRect();
      const dropdownContainer = container.querySelector('.dropdownContainer') as HTMLElement;
      const containerOffset = dropdownContainer.getBoundingClientRect();
      
      // Calculate position relative to the dropdown container
      const dropdownWidth = dropdown.offsetWidth || 200; // Fallback width
      const leftPosition = elRect.left - containerOffset.left + (elRect.width / 2) - (dropdownWidth / 2);
      const maxLeft = window.innerWidth - dropdownWidth - 20; // Ensure it doesn't go off screen right
      const finalLeft = Math.max(10, Math.min(leftPosition, maxLeft));
      
      dropdown.style.left = `${finalLeft}px`;
      
      // Position the arrow to point to the center of the menu item
      const arrowPosition = elRect.left - containerOffset.left + (elRect.width / 2) - finalLeft - 6;
      dropdown.style.setProperty('--arrow-left', `${Math.max(12, Math.min(arrowPosition, dropdownWidth - 12))}px`);
    }
  }
}

function closeDropdown() {
  if (!globalNavRef.value) return;
  const container = globalNavRef.value;
  
  // Remove active class from all elements
  const allRoots = Array.from(container.querySelectorAll('.hasDropdown'));
  allRoots.forEach(r => r.classList.remove('active'));
  
  const allDropdowns = Array.from(container.querySelectorAll('.dropdownSection'));
  allDropdowns.forEach(d => d.classList.remove('active'));

  container.classList.remove('dropdownActive');
  activeDropdownEl = null;
}

function toggleDropdown(el: HTMLElement) {
  if (activeDropdownEl === el) {
    closeDropdown();
  } else {
    openDropdown(el);
  }
}

// Hover timeout functions
function startCloseTimeout() {
  closeDropdownTimeout = window.setTimeout(() => {
    closeDropdown();
  }, 150);
}

function stopCloseTimeout() {
  if (closeDropdownTimeout) {
    clearTimeout(closeDropdownTimeout);
    closeDropdownTimeout = null;
  }
}

onMounted(() => {
  const container = globalNavRef.value;
  if (!container) return;

  const hasDropdowns = Array.from(container.querySelectorAll('.hasDropdown'));
  hasDropdowns.forEach(el => {
    // Click events
    el.addEventListener('click', (ev: Event) => {
      ev.preventDefault();
      ev.stopPropagation();
      toggleDropdown(el as HTMLElement);
    });
    
    // Hover events
    el.addEventListener('mouseenter', () => {
      stopCloseTimeout();
      openDropdown(el as HTMLElement);
    });
    
    el.addEventListener('mouseleave', () => {
      startCloseTimeout();
    });
  });

  // Dropdown container events
  const dropdownContainer = container.querySelector('.dropdownContainer');
  if (dropdownContainer) {
    dropdownContainer.addEventListener('click', ev => {
      ev.stopPropagation();
    });
    dropdownContainer.addEventListener('mouseenter', () => {
      stopCloseTimeout();
    });
    dropdownContainer.addEventListener('mouseleave', () => {
      startCloseTimeout();
    });
  }

  // Close dropdown if click outside
  document.body.addEventListener('click', () => {
    closeDropdown();
  });
});

function redirect(path: string) {
  if (path) {
    router.push(path);
    closeDropdown();
  }
}

// Get the current route for active state checking
const route = useRoute();

// Helper function to check if a menu item is active
function isActive(path: string): boolean {
  if (path === '/our-service') {
    return route.name === 'our-service' || route.path === '/our-service';
  }
  if (path === '/about-us') {
    return route.name === 'about-us' || route.path === '/about-us';
  }
  if (path.includes('/products')) {
    return route.path === '/products';
  }
  if (path === '/blog') {
    return route.path.startsWith('/blog');
  }
  if (path === '/media') {
    return route.path.startsWith('/media');
  }
  return route.path === path || route.path.startsWith(path + '/');
}
</script>

<template>
  <header
    ref="globalNavRef"
    class="globalNav globalNav--medium"
    :class="{'header-fixed': headerFixed}"
    @click.stop
  >
    <div class="container-lg">
      <ul class="navRoot">
        <li class="navSection primary sec-primary-menu">
          <a @click="redirect('/')" class="nav-item rootLink item-home colorize flex items-center" :class="{ active: isActive('/') }"><span>Home</span></a>
          <a class="nav-item rootLink item-about hasDropdown colorize flex items-center" data-dropdown="about" :class="{ active: isActive('/about-us') || isActive('/our-service') }"><span>About</span></a>
          <a class="nav-item rootLink item-solutions hasDropdown colorize flex items-center" data-dropdown="solutions" :class="{ active: String(route.path).startsWith('/products/') }"><span>Catalogue</span></a>
          <a @click="redirect('/services')" class="nav-item rootLink item-media colorize flex items-center" :class="{ active: isActive('/services') }"><span>Services</span></a>
          <a @click="redirect('/products')" class="nav-item rootLink item-products hasDropdown colorize flex items-center" :class="{ active: isActive('/products') }"><span>Products</span></a>
          <!-- <a class="nav-item rootLink item-media hasDropdown colorize flex items-center" data-dropdown="media"><span>Media</span></a> -->
        </li>
      </ul>
    </div>

    <!-- Simple dropdown container -->
    <div class="dropdownContainer">
      <!-- About dropdown -->
      <div class="dropdownSection about" data-dropdown="about">
        <div class="dropdownContent">
          <ul class="simpleDropdown">
            <li v-for="item in menuItems.about" :key="item.path">
              <a @click="redirect(item.path)" class="dropdown-item" :class="{ active: isActive(item.path) }">
                <div class="item-icon">
                  <!-- 360 Services SVG -->
                  <svg v-if="item.icon === 'services'" width="18" height="18">
                    <path fill="#87BBFD" class="hover-fillLight" d="M.945 1.998h3.632C6.744 1.998 8.5 3.005 8.5 4.83v11.583c-.512 0-1.015-.337-1.33-.59-1.03-.828-3.057-.828-5.222-.828H.945A.944.944 0 0 1 0 14.052V2.944C0 2.422.423 2 .945 2z"></path>
                    <path fill="#6772E5" class="hover-fillDark" d="M16.055 1.998h-3.632C10.257 1.998 8.5 3.005 8.5 4.83v11.583c.512 0 1.015-.337 1.33-.59 1.03-.828 3.057-.828 5.222-.828h1.003A.944.944 0 0 0 17 14.05V2.945A.944.944 0 0 0 16.055 2z"></path>
                  </svg>
                  <!-- ISolveIcon for other items -->
                  <AltraIcon v-else-if="item.icon === 'about'" class="w-18px h-18px" />
                </div>
                <span>{{ item.label }}</span>
              </a>
            </li>
          </ul>
        </div>
      </div>

      <!-- Solutions dropdown -->
      <div class="dropdownSection solutions" data-dropdown="solutions">
        <div class="dropdownContent">
          <ul class="simpleDropdown simpleDropdown--columns">
            <li v-for="item in menuItems.catelogue" :key="item.path">
              <a @click="redirect(item.path)" class="dropdown-item" :class="{ active: isActive(item.path) }">
                <div class="item-icon">
                  <iconSolution v-if="item.icon === 'ethanol'" class="w-18px h-18px" />
                  <iconChemicals v-else-if="item.icon === 'chemicals'" class="w-18px h-18px" />
                  <iconManufactured v-else-if="item.icon === 'manufactured'" class="w-18px h-18px" />
                  <iconManufacturing v-else-if="item.icon === 'manufacturing'" class="w-18px h-18px" />
                  <iconIndustry v-else-if="item.icon === 'industry'" class="w-18px h-18px" />
                  <iconApplications v-else-if="item.icon === 'applications'" class="w-18px h-18px" />
                  <AltraIcon v-else-if="item.icon === 'about'" class="w-18px h-18px" />
                </div>
                <span>{{ item.label }}</span>
              </a>
            </li>
          </ul>
        </div>
      </div>

      <!-- Products dropdown -->
      <div class="dropdownSection products" data-dropdown="products">
        <div class="dropdownContent">
          <ul class="simpleDropdown">
            <li v-for="item in menuItems.products" :key="item.path">
              <a @click="redirect(item.path)" class="dropdown-item" :class="{ active: isActive(item.path) }">
                <div class="item-icon">
                  <AltraIcon class="w-18px h-18px" />
                </div>
                <span>{{ item.label }}</span>
              </a>
            </li>
          </ul>
        </div>
      </div>
      
      <!-- Media dropdown -->
      <div class="dropdownSection media" data-dropdown="media">
        <div class="dropdownContent">
          <ul class="simpleDropdown">
            <li v-for="item in menuItems.media" :key="item.path">
              <a @click="redirect(item.path)" class="dropdown-item" :class="{ active: isActive(item.path) }">
                <div class="item-icon">
                    <AltraIcon class="w-18px h-18px" />
                </div>
                <span>{{ item.label }}</span>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped lang="scss">
/* Copy exact styles from StripeMegaMenuV1 with modifications for traditional dropdowns */
.navSection.primary.sec-primary-menu {
  display: flex;
  height: 50px;

  .nav-item {
    &.active {
      > span {
        color: var(--c-sky-blue) !important;
      }
    }
  }
}

.globalNav {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  z-index: 1;
  height: 100px;
  perspective: 2000px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: height 0.3s ease;


  ul {
    padding: 0;
    margin: 0;
  }

  li {
    list-style: none;
  }

  a {
    text-decoration: none;
    -webkit-tap-highlight-color: transparent;
    color: #6772e5;
    transition: color .1s;

    &:hover {
      color: #32325d;
    }
  }

  > .container-lg {
    padding: 0;
  }

  .navRoot {
    position: relative;
  }

  .navSection.primary {
    display: flex;
    justify-content: center;
  }

  .rootLink {
    display: inline-block;
    height: 50px;
    white-space: nowrap;
    padding: 0 20px;
    line-height: 50px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: color .1s;
    position: relative;

    &:hover,
    &.active {
      color: #32325d;
    }

    span {
      display: block;
      transition: transform .1s;

      color: white;
    }

    &:hover span,
    &.active span {
      transform: translateY(-1px);
    }
  }

  /* Traditional dropdown positioning - positioned relative to the parent link */
  .dropdownContainer {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 100;
    pointer-events: none;
  }

  &.dropdownActive .dropdownContainer {
    pointer-events: auto;
  }

  .dropdownSection {
    position: absolute;
    top: 10px;
    background: white;
    border-radius: 6px;
    box-shadow: 0 50px 100px rgba(50, 50, 93, 0.1), 0 15px 35px rgba(50, 50, 93, 0.15), 0 5px 15px rgba(0, 0, 0, 0.1);
    padding: 0;
    opacity: 0;
    visibility: hidden;
    transform: translateY(-10px);
    transition: all 0.15s ease;
    border: 1px solid rgba(255, 255, 255, 0.8);

    &.active {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }

    /* Dynamic positioning will be handled by JavaScript */
    /* Remove fixed positioning classes */
  }

  .dropdownContent {
    position: relative;
  }

  .simpleDropdown {
    list-style: none;
    margin: 0;
    padding: 8px 0;
    min-width: 180px;

    &--columns {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0 15px;
      min-width: 320px;
      padding: 12px 0;
    }

    li {
      margin: 0;
    }

    .dropdown-item {
      display: flex;
      align-items: center;
      padding: 10px 16px;
      color: var(--base-650);
      text-decoration: none;
      transition: all 0.1s ease;
      font-size: fs(-1);
      line-height: 1.4;
      cursor: pointer;
      white-space: nowrap;
      font-weight: 500;
      gap: 12px;

      .item-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        flex-shrink: 0;

        svg {
          width: 18px;
          height: 18px;
        }

        /* Icon color transitions */
        svg path {
          transition: fill 0.1s ease;
        }
      }

      span {
        flex: 1;
      }

      &:hover {
        color: var(--c-sky-blue);

        .item-icon svg path {
          &.hover-fillLight {
            fill: var(--c-sky-blue);
          }
          &.hover-fillDark {
            fill: var(--c-sky-blue);
          }
        }
      }

      &.active {
        color: var(--c-sky-blue);
        font-weight: 600;

        .item-icon svg path {
          &.hover-fillLight {
            fill: var(--c-sky-blue);
          }
          &.hover-fillDark {
            fill: var(--c-sky-blue);
          }
        }
      }

      &:active {
        background-color: #e6ebf1;
      }
    }
  }

  /* Add small arrow pointing up to the active menu item */
  .dropdownSection::before {
    content: '';
    position: absolute;
    top: -6px;
    left: var(--arrow-left, 20px);
    width: 12px;
    height: 12px;
    background: white;
    border: 1px solid rgba(255, 255, 255, 0.8);
    border-bottom: none;
    border-right: none;
    transform: rotate(45deg);
    box-shadow: -2px -2px 5px rgba(50, 50, 93, 0.05);
  }

  &.header-fixed {
    height: 80px;

    .rootLink {
      span {
        color: var(--base-650);
      }
    }
  }
}

/* Maintain same responsive behavior */
@media (max-width: 670px) {
  .globalNav {
    display: none;
  }
}

@media (min-width: 670px) {
  .globalNav .navSection.primary {
    display: flex;
  }
}

/* Background overlay when dropdown is active (subtle) */
.globalNav.dropdownActive::after {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.02);
  z-index: -1;
  pointer-events: none;
}
</style>